import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Hotel, Calendar, Users, Shield, CheckCircle, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const HotelsIndustry = () => {
  const features = [
    { icon: Calendar, title: 'Housekeeping Coordination', description: 'Optimize room cleaning schedules and turnover times' },
    { icon: Users, title: 'Multi-Department Scheduling', description: 'Coordinate front desk, maintenance, and concierge teams' },
    { icon: Shield, title: 'Compliance Tracking', description: 'Maintain certifications and safety training records' },
    { icon: Award, title: 'Guest Satisfaction Analytics', description: 'Link staffing levels to guest experience scores' }
  ];

  const benefits = [
    '45% improvement in room turnover efficiency',
    '28% reduction in overtime costs',
    '98% guest satisfaction scores',
    'Real-time room status updates',
    'Automated maintenance scheduling',
    'Staff performance tracking'
  ];

  const stats = [
    { value: '300+', label: 'Hotels' },
    { value: '45%', label: 'Faster Turnover' },
    { value: '98%', label: 'Guest Satisfaction' },
    { value: '28%', label: 'Overtime Reduction' }
  ];

  return (
    <>
      <LandingNav />
      
      <section className="pt-24 pb-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center space-x-2 mb-4">
                <Hotel className="w-8 h-8" />
                <span className="text-purple-200 font-semibold">Hotels & Resorts</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Deliver Exceptional Guest Experiences with Smart Staffing
              </h1>
              <p className="text-xl text-purple-100 mb-8">
                Manage your hotel operations seamlessly from front desk to housekeeping with intelligent workforce management tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center">
                  Schedule a Demo
                </Link>
                <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center">
                  View Pricing
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <img src="/Admin Dashboard.png" alt="Hotel Management Dashboard" className="rounded-2xl shadow-2xl border-4 border-white/20" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Hotel Operations Management</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From check-in to check-out, keep every department running smoothly</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}>
              <img src="/Analyticspage.png" alt="Analytics Dashboard" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Measurable Impact on Operations</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-purple-600 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            "Shiftry transformed our hotel operations. We've reduced overtime costs and our guest satisfaction scores have never been higher."
          </blockquote>
          <p className="text-lg text-gray-600">- Michael Chen, General Manager, Grand Plaza Hotel</p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Elevate Your Hotel Operations?</h2>
          <p className="text-xl text-purple-100 mb-8">Join 300+ hotels delivering exceptional guest experiences</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Schedule Your Free Demo
            </Link>
            <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default HotelsIndustry;
